import { useId, useMemo, useState } from "react";

import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { IconButton } from "@/components/ui/icon-button";
import { MoreVertGlyph } from "@/components/ui/icon-menu-button";
import { MoreGroupMenu } from "@/components/ui/more-group-menu";
import { MuiButton } from "@/components/ui/mui-button";
import { NameNoteDialog, type NameNoteValues } from "@/components/ui/name-note-dialog";
import type { SpecType } from "@/api/contracts/specifications";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import {
  useCreateSpecGroup,
  useDeleteSpecGroup,
  useResetSpecGroup,
  useUpdateSpecGroup,
  useUpdateSpecGroupStatus,
} from "@/hooks/spec-editor-groups";
import type { SpecLinesGroup } from "@/hooks/specifications";
import { ERRORS } from "@/lib/spec-editor-errors";

// The Editor's group controls (#805): `Add group` and its dialog
// (`Header/AddGroup/AddGroup.tsx`), and a group's ⋮ with what it opens
// (`MainContent/Groups/GroupActionsMenu.tsx`, `EditGroup.tsx`,
// `DeleteGroup.tsx`), wired to #799's group verbs. Every failure reaches the
// screen as its `ERRORS` string (#548 D1) on the shared `ErrorSnackbar`; the
// donor's extra `Portal` round two of them is not reproduced (structure rulings
// item 9).
//
// **Nothing here guesses** (structure rulings item 8; ADR-0014 decision 5 as
// amended): Add, Edit and Delete hold their dialogs open until the server
// answers and stay open, input kept, on a failure; Mark group as closes the
// menu at the click and the lines change when the result lands. The donor's
// Edit closed at once over an optimistic write — the owner-ruled break.
//
// Every write names its target when it is sent (`useSpecEditorTarget`), so the
// cursor it carries is the view's as of the click.

const NO_NOTE: NameNoteValues = { name: "", notes: "" };

/**
 * `Add group` and its `Add new group` dialog. #806 places it in the toolbar
 * row.
 *
 * The button (`AddGroup.tsx:64-72`): contained, large, primary, `px: 3, py:
 * 1.8` and `mr: 28px !important` from `lg` up. The dialog is mounted only
 * while it is open or leaving, and its form resets on the exit — so a cancel
 * or a success leaves the next opening empty, and a failure keeps what was
 * typed.
 */
export function AddSpecGroup({ target }: { target: SpecEditorTarget }) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const create = useCreateSpecGroup();

  return (
    <>
      <MuiButton
        variant="contained"
        size="large"
        data-testid="add-group-btn"
        className="px-6 py-[14.4px] lg:mr-[28px]"
        onClick={() => {
          setMounted(true);
          setOpen(true);
        }}
      >
        Add group
      </MuiButton>
      {mounted ? (
        <NameNoteDialog
          open={open}
          onOpenChange={setOpen}
          title="Add new group"
          submitLabel="Add Group"
          enableClearNote={false}
          saving={create.isPending}
          seed={NO_NOTE}
          onSubmit={(values) =>
            create.mutate({ ...target(), ...values }, { onSuccess: () => setOpen(false) })
          }
          onExited={() => setMounted(false)}
        />
      ) : null}
      <ErrorSnackbar open={create.isError} message={ERRORS.ADD_GROUP} onClose={create.reset} />
    </>
  );
}

/**
 * A group's ⋮ (`GroupActionsMenu.tsx:72-85`): `More options`, `MoreVert` in
 * `action.active`, `aria-haspopup`, `aria-expanded` while open. Pressing it
 * while the menu is open does nothing (`handleOpen`'s guard), and the menu's
 * click-away ignores it.
 *
 * `aria-controls` points at the menu — a recorded a11y-tree-only break: the
 * donor's names `positioned-menu`, an id nothing carries (#805 Assumption 5).
 *
 * `Edit Group Name/Note` and `Delete` only open their dialogs, as the donor's
 * do: the menu closes when the dialog takes the focus, which is its
 * click-away's `focusin`.
 *
 * **Reset group name and notes** (#1084; a named scope addition, #1060 Q3,
 * Q5–Q6) is offered on a group flagged changed from base alone, `…to Base` on
 * a community spec and `…to Core` on a base. It runs at once with no dialog,
 * as the line ⋮'s resets do: the row spins with the menu held open, the menu
 * closes when the answer arrives, and a failure leaves it open over its
 * snackbar.
 */
export function SpecGroupActions({
  group,
  target,
  lifted = false,
  specType,
}: {
  group: SpecLinesGroup;
  target: SpecEditorTarget;
  /** Drawn on the copy in the air: the same ⋮, without the slot's id. */
  lifted?: boolean;
  /** The spec's type, which names the reset row's parent. */
  specType?: SpecType;
}) {
  const menuId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const status = useUpdateSpecGroupStatus();
  const reset = useResetSpecGroup();
  const resetLabel = specType ? RESET_GROUP_TEXT[specType] : "";

  const triggerId = lifted ? undefined : `group-${group.id}-more-menu`;

  return (
    <>
      <MoreGroupMenu
        id={menuId}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        status={group.status}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDeleteOpen(true)}
        onStatus={(next) => {
          setMenuOpen(false);
          status.mutate({ ...target(), groupId: group.id, status: next });
        }}
        reset={
          group.changedFromBase && resetLabel
            ? {
                label: resetLabel,
                pending: reset.isPending,
                onReset: () =>
                  reset.mutate(
                    { ...target(), groupId: group.id },
                    { onSuccess: () => setMenuOpen(false) },
                  ),
              }
            : undefined
        }
        anchor={
          <IconButton
            id={triggerId}
            data-testid={triggerId}
            aria-label="More options"
            aria-haspopup="true"
            aria-expanded={menuOpen ? true : undefined}
            aria-controls={menuOpen ? menuId : undefined}
            onClick={() => {
              if (!menuOpen) setMenuOpen(true);
            }}
          >
            <MoreVertGlyph />
          </IconButton>
        }
      />
      <EditSpecGroupDialog
        group={group}
        target={target}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
      <DeleteSpecGroupDialog
        group={group}
        target={target}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
      <ErrorSnackbar
        open={status.isError}
        message={ERRORS.UPDATE_GROUP_STATUS}
        onClose={status.reset}
      />
      <ErrorSnackbar open={reset.isError} message={ERRORS.RESET_GROUP} onClose={reset.reset} />
    </>
  );
}

/** The reset row's label by spec type, as the line ⋮'s `RESET_TEXT`. A core
 *  has no parent, so no group of its ever flags. */
const RESET_GROUP_TEXT: Record<SpecType, string> = {
  community: "Reset group name and notes to Base",
  base: "Reset group name and notes to Core",
  core: "",
};

/**
 * `Edit group / note` (`EditGroup.tsx`): `Save` and `Clear Note`, the form
 * seeded from the cached group whenever that group changes and never reset on
 * a close — a cancelled edit is still there next time, until the group
 * changes. The write is awaited: Save spins, a success closes, a failure stays
 * open with what was typed.
 */
function EditSpecGroupDialog({
  group,
  target,
  open,
  onOpenChange,
}: {
  group: SpecLinesGroup;
  target: SpecEditorTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateSpecGroup();
  // The donor's `useEffect([group])`: a new group object is a new seed.
  // Keyed on the group object, as the donor's `useEffect([group])` is
  // (`EditGroup.tsx:32-35`): every lines page rebuilds every group there
  // (`objectCache.ts:60-71`), and here, so a page load reseeds the form.
  const seed = useMemo(() => ({ name: group.name || "", notes: group.notes || "" }), [group]);

  return (
    <>
      <NameNoteDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Edit group / note"
        submitLabel="Save"
        saving={update.isPending}
        seed={seed}
        onSubmit={(values) =>
          update.mutate(
            { ...target(), groupId: group.id, ...values },
            { onSuccess: () => onOpenChange(false) },
          )
        }
      />
      <ErrorSnackbar open={update.isError} message={ERRORS.EDIT_GROUP} onClose={update.reset} />
    </>
  );
}

/**
 * `Delete {name}` (`DeleteGroup.tsx`): the shared confirm, the name as stored
 * — a trailing space lands before the `?`. Measured on the running donor, it
 * is content-sized rather than full-width and opens on its paper rather than
 * on `Delete` (#805 enumeration E4). Delete spins and Cancel is held while the
 * write is in the air; the X, Escape and the backdrop are not.
 */
function DeleteSpecGroupDialog({
  group,
  target,
  open,
  onOpenChange,
}: {
  group: SpecLinesGroup;
  target: SpecEditorTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const remove = useDeleteSpecGroup();

  return (
    <>
      <ConfirmDeleteDialog
        open={open}
        onOpenChange={onOpenChange}
        label={group.name}
        fullWidth={false}
        autoFocusConfirm={false}
        isDeleting={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { ...target(), groupId: group.id },
            { onSuccess: () => onOpenChange(false) },
          )
        }
      />
      <ErrorSnackbar open={remove.isError} message={ERRORS.DELETE_GROUP} onClose={remove.reset} />
    </>
  );
}
